import { PixelRatio, Platform, useWindowDimensions } from 'react-native'

const guidelineBaseWidth = 375
const guidelineBaseHeight = 812

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max)
}

interface ResponsiveOptions {
  containerWidth?: number
  minScale?: number
  maxScale?: number
  minVerticalScale?: number
  maxVerticalScale?: number
}

export const useResponsive = (options: ResponsiveOptions = {}) => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const { containerWidth, minScale = 0.8, maxScale = 1.2, minVerticalScale = 0.8, maxVerticalScale = 1.2 } = options

  // Use container width if provided, otherwise use screen width
  const baseWidth = containerWidth || screenWidth
  const responsiveScreenWidth = Platform.OS === 'web' ? (baseWidth > 600 ? 600 : baseWidth) : baseWidth

  const wp = (percent: number) => PixelRatio.roundToNearestPixel((screenWidth * percent) / 100)

  const hp = (percent: number) => PixelRatio.roundToNearestPixel((screenHeight * percent) / 100)

  const scale = (size: number) => {
    const scaledSize = (responsiveScreenWidth / guidelineBaseWidth) * size
    return clamp(scaledSize, size * minScale, size * maxScale)
  }

  const verticalScale = (size: number) => {
    const scaledSize = (screenHeight / guidelineBaseHeight) * size
    return clamp(scaledSize, size * minVerticalScale, size * maxVerticalScale)
  }

  const moderateScale = (size: number, factor = 0.5) => {
    // First apply vertical scaling as the primary consideration
    const verticalScaledSize = verticalScale(size)
    // Then apply horizontal scaling with a reduced factor
    const horizontalScaledSize = size + (scale(size) - size) * factor
    // Use the smaller of the two to ensure content fits
    return Math.min(verticalScaledSize, horizontalScaledSize)
  }

  return { wp, hp, scale, verticalScale, moderateScale, screenWidth, screenHeight }
}
